/** Cosmetic presets shared by the creator, explorer and procedural human renderer. */
export const humanMakeup = [
  { id: 'none', name: 'None' },
  { id: 'rose', name: 'Rose velvet', shadow: 0x9c4864, lips: 0xa73355, accent: 0xe8b6aa },
  { id: 'ember', name: 'Ember gold', shadow: 0xa34e28, lips: 0x8c3028, accent: 0xe6bd65 },
  { id: 'amethyst', name: 'Amethyst', shadow: 0x633d91, lips: 0x733255, accent: 0xc4a0df },
  { id: 'midnight', name: 'Midnight kohl', shadow: 0x253344, lips: 0x412b49, accent: 0xa6becb },
  { id: 'warpaint', name: 'Crimson', shadow: 0x742c30, lips: 0x673038, accent: 0xc53d38 },
  { id: 'jade', name: 'Jade & bronze', shadow: 0x276b56, lips: 0x784333, accent: 0xd6b470 },
  { id: 'azure', name: 'Azure & silver', shadow: 0x296caa, lips: 0x514378, accent: 0xc1dbe8 },
  { id: 'plum', name: 'Plum noir', shadow: 0x49283e, lips: 0x4d1832, accent: 0xbb739d },
  { id: 'pearl', name: 'Pearl & blush', shadow: 0xae8c9c, lips: 0xbc6777, accent: 0xf1ded3 },
  { id: 'ochre', name: 'Ochre & charcoal', shadow: 0xb77d31, lips: 0x533c2e, accent: 0xf0c775 },
  { id: 'obsidian', name: 'Obsidian', shadow: 0x201d26, lips: 0x211827, accent: 0x807487 },
  { id: 'ivory', name: 'Ivory & vermilion', shadow: 0xe1d6c1, lips: 0xac332b, accent: 0xf4eee3 },
];

/** Geometry and palette are independent: every style can wear every colour scheme. */
export const humanMakeupStyles = [
  { id: 'none', name: 'None' }, { id: 'soft', name: 'Soft wash' }, { id: 'winged', name: 'Winged liner' },
  { id: 'smoky', name: 'Smoky eyes' }, { id: 'cat-eye', name: 'Dramatic cat eyes' },
  { id: 'double-wing', name: 'Double wings' }, { id: 'lips', name: 'Statement lips' },
  { id: 'warpaint', name: 'Battle stripes' }, { id: 'ritual', name: 'Ritual mask' },
  { id: 'harlequin', name: 'Harlequin tears' }, { id: 'sunburst', name: 'Sunburst eyes' },
];

/**
 * Ink in a unit square (y up), drawn in order into the design's texture: a stroke runs along its points at its width, tapering by
 * `ends` (width multipliers at its first and last point); `fill` closes the points into a solid shape; `cut` clears what it covers.
 */
/** Clear skin round a design inside its texture, in design units, so a decal's edge never shows. */
export const INK_MARGIN = .08;
export interface InkStroke { points: [number, number][]; width: number; ends?: [number, number]; fill?: boolean; cut?: boolean }
type Point = [number, number];
const line = (points: Point[], width = .03, ends?: [number, number]): InkStroke => ({ points, width, ends });
const curve = (count: number, at: (t: number) => Point) => Array.from({ length: count + 1 }, (_, i) => at(i / count));
const arc = (cx: number, cy: number, rx: number, ry: number, from: number, to: number, width = .03, ends?: [number, number]) =>
  line(curve(40, t => { const a = from + (to - from) * t; return [cx + Math.cos(a) * rx, cy + Math.sin(a) * ry]; }), width, ends);
const shape = (points: Point[], cut = false): InkStroke => ({ points, width: 0, fill: true, cut });
const disc = (cx: number, cy: number, r: number, cut = false) => shape(curve(36, t => [cx + Math.cos(t * Math.PI * 2) * r, cy + Math.sin(t * Math.PI * 2) * r]), cut);
const dot = (x: number, y: number, r = .022) => disc(x, y, r);
/** A leaf or blade between two tips, bowed `bulge` to each side (negative bulges one side inward). */
const lens = (a: Point, b: Point, bulge: number, inner = bulge) => {
  const dx = b[0] - a[0], dy = b[1] - a[1], length = Math.hypot(dx, dy), nx = -dy / length, ny = dx / length;
  const side = (amount: number, flip: number) => curve(16, t => { const w = Math.sin(t * Math.PI) ** .8 * amount * length * flip; return [a[0] + dx * t + nx * w, a[1] + dy * t + ny * w]; });
  return shape([...side(bulge, 1), ...side(inner, -1).reverse().slice(1, -1)]);
};
/** A four-pointed glint. */
const glint = (x: number, y: number, r: number) => shape(curve(16, t => { const a = t * Math.PI * 2, k = r * (.22 + .78 * Math.abs(Math.cos(2 * a)) ** 3); return [x + Math.cos(a) * k, y + Math.sin(a) * k]; }));
const erase = (stroke: InkStroke): InkStroke => ({ ...stroke, cut: true });
const mirror = (strokes: InkStroke[]) => [...strokes, ...strokes.map(stroke => ({ ...stroke, points: stroke.points.map(([x, y]) => [1 - x, y] as Point) }))];

export interface TattooPreset {
  id: string; name: string; ink?: number; strokes: InkStroke[];
  /** Where this motif sits when the layout includes face ink. */
  placement?: 'forehead' | 'cheek';
}
const starMap: Point[] = [[.16, .2], [.36, .38], [.31, .66], [.55, .58], [.8, .74], [.7, .32], [.86, .14]];
export const humanTattoos: TattooPreset[] = [
  { id: 'none', name: 'None', strokes: [] },
  { id: 'lunar', name: 'Moon covenant', ink: 0x344768, placement: 'forehead', strokes: [
    disc(.47, .5, .3), disc(.6, .56, .26, true),
    arc(.5, .5, .43, .43, Math.PI * .62, Math.PI * 1.38, .018, [.2, .2]), arc(.5, .5, .43, .43, -Math.PI * .38, Math.PI * .38, .018, [.2, .2]),
    glint(.74, .3, .07), dot(.83, .5, .016), dot(.71, .7, .013), dot(.5, .95, .02), dot(.5, .05, .02),
  ] },
  { id: 'stars', name: 'Star map', ink: 0x3c5266, placement: 'cheek', strokes: [
    line(starMap.slice(0, 5), .012), line([starMap[3], starMap[5], starMap[6]], .012), line([starMap[1], starMap[5]], .009),
    ...starMap.map(([x, y], i) => i === 3 ? glint(x, y, .11) : dot(x, y, [.032, .026, .022, 0, .03, .024, .02][i])),
    dot(.12, .88, .01), dot(.46, .9, .008), dot(.9, .46, .009), dot(.62, .1, .008),
  ] },
  { id: 'runes', name: 'Oath runes', ink: 0x293f43, placement: 'cheek', strokes: [
    // Algiz, Tiwaz and Othala on one stave, between binding dots.
    line([[.5, .03], [.5, .97]], .045, [.6, .6]),
    line([[.5, .78], [.28, .95]], .04, [1, .5]), line([[.5, .78], [.72, .95]], .04, [1, .5]),
    line([[.29, .5], [.5, .66], [.71, .5]], .04, [.5, .5]),
    line([[.5, .4], [.3, .26], [.5, .1], [.7, .26], [.5, .4]], .04), line([[.42, .14], [.27, .03]], .04, [1, .4]), line([[.58, .14], [.73, .03]], .04, [1, .4]),
    dot(.36, .72, .02), dot(.64, .72, .02), dot(.5, .46, .018),
  ] },
  { id: 'thorns', name: 'Briar sigil', ink: 0x354535, placement: 'cheek', strokes: [
    line(curve(40, t => [.5 + Math.sin(t * Math.PI * 2.5) * .16, .03 + t * .94]), .05, [1.1, .35]),
    ...[.1, .26, .42, .58, .74, .88].map((t, i) => {
      const y = .03 + t * .94, x = .5 + Math.sin(t * Math.PI * 2.5) * .16, side = i % 2 ? 1 : -1;
      return shape([[x, y - .04], [x + side * .15, y + .07], [x + side * .02, y + .035], [x, y + .04]]);
    }),
    lens([.42, .36], [.14, .46], .32), lens([.6, .66], [.88, .6], .32), lens([.54, .92], [.3, .99], .3),
  ] },
  { id: 'sun', name: 'Dawn seal', ink: 0x694437, placement: 'forehead', strokes: [
    arc(.5, .5, .2, .2, 0, Math.PI * 2, .05), dot(.5, .5, .07),
    ...Array.from({ length: 12 }, (_, i) => {
      const a = i * Math.PI / 6 + Math.PI / 2, long = i % 2 === 0, r0 = .27, r1 = long ? .48 : .38, half = long ? .05 : .035;
      return shape([[.5 + Math.cos(a - half) * r0, .5 + Math.sin(a - half) * r0], [.5 + Math.cos(a) * r1, .5 + Math.sin(a) * r1], [.5 + Math.cos(a + half) * r0, .5 + Math.sin(a + half) * r0]]);
    }),
  ] },
  { id: 'serpent', name: 'Coiled serpent', ink: 0x354e46, placement: 'cheek', strokes: [
    line(curve(60, t => [.5 + Math.sin(t * Math.PI * 3.2) * (.3 - t * .2), .8 - t * .77]), .12, [1, .06]),
    // Belly scales: pale notches down the coils.
    ...Array.from({ length: 10 }, (_, i) => .06 + i * .08).map(t => erase(dot(.5 + Math.sin(t * Math.PI * 3.2) * (.3 - t * .2), .8 - t * .77, .018 * (1 - t * .7)))),
    shape([[.5, .76], [.36, .86], [.4, .97], [.56, 1], [.66, .9], [.6, .78]]), erase(dot(.49, .9, .02)),
    line([[.4, .97], [.33, 1], [.3, 1.03]], .01), line([[.33, 1], [.29, .98]], .01),
  ] },
  { id: 'dragon', name: 'Dragon crest', ink: 0x483642, placement: 'forehead', strokes: [
    ...mirror([
      shape([[.46, .62], [.3, .8], [.06, .95], [.12, .74], [.02, .58], [.18, .55], [.1, .38], [.3, .44], [.44, .5]]),
      // The finger bones show as clear skin through the membrane.
      erase(line([[.42, .58], [.14, .8]], .016, [1, .3])), erase(line([[.4, .55], [.14, .6]], .016, [1, .3])), erase(line([[.4, .52], [.22, .44]], .016, [1, .3])),
    ]),
    line(curve(30, t => [.5 + Math.sin(t * Math.PI * 2) * .1, .7 - t * .67]), .1, [1, .08]),
    shape([[.5, .64], [.41, .75], [.43, .9], [.5, .98], [.57, .9], [.59, .75]]), erase(dot(.46, .83, .015)), erase(dot(.54, .83, .015)),
    line([[.44, .92], [.38, 1]], .02, [1, .2]), line([[.56, .92], [.62, 1]], .02, [1, .2]),
  ] },
  { id: 'tribal', name: 'Blackwork blades', ink: 0x252b31, placement: 'cheek', strokes: [
    lens([.5, .99], [.5, .04], .07),
    ...mirror([
      line(curve(20, t => [.46 - t * .34, .2 + t * .2 + Math.sin(t * Math.PI) * .2]), .13, [1, .02]),
      line(curve(20, t => [.45 - t * .3, .5 + t * .14 + Math.sin(t * Math.PI) * .18]), .1, [1, .02]),
      line(curve(20, t => [.46 - t * .22, .72 + t * .08 + Math.sin(t * Math.PI) * .14]), .07, [1, .02]),
    ]),
  ] },
  { id: 'rose-vine', name: 'Rose & vine', ink: 0x543944, placement: 'cheek', strokes: [
    line(curve(30, t => [.5 + Math.sin(t * Math.PI * 1.6) * .1, .5 - t * .48]), .045, [1, .3]),
    lens([.48, .3], [.16, .2], .42), lens([.56, .16], [.86, .1], .42),
    erase(line([[.48, .3], [.24, .23]], .012)), erase(line([[.56, .16], [.79, .12]], .012)),
    disc(.5, .7, .25),
    // Petals open from the centre as clear skin.
    ...[0, 1, 2].map(i => erase(arc(.5, .7 - i * .012, .19 - i * .055, .16 - i * .045, Math.PI * (.15 + i * .3), Math.PI * (1.65 + i * .3), .03, [.2, 1]))),
  ] },
  { id: 'feather', name: 'Raven feather', ink: 0x333c50, placement: 'cheek', strokes: [
    shape([...curve(18, t => [.24 + t * .46 - Math.sin(t * Math.PI) * .2, .08 + t * .9]), ...curve(18, t => [.7 - t * .46 + Math.sin(t * Math.PI) * .14, .98 - t * .9]).slice(1)]),
    ...[.3, .5, .66].map(t => erase(shape([[.47 + t * .23 - .22 * Math.sin(t * Math.PI), .08 + t * .9], [.32 + t * .3 - .2 * Math.sin(t * Math.PI), .03 + t * .9], [.4 + t * .3 - .2 * Math.sin(t * Math.PI), .12 + t * .9]]))),
    ...[.4, .58].map(t => erase(line([[.24 + t * .46, .08 + t * .9], [.54 + t * .3, .0 + t * .9]], .01))),
    erase(line([[.2, .04], [.7, .98]], .04, [1.2, .3])), line([[.18, 0], [.7, .98]], .018, [1.3, .3]),
  ] },
  { id: 'labyrinth', name: 'Sacred labyrinth', ink: 0x334b60, placement: 'forehead', strokes: [
    ...[.14, .23, .32, .41].map((r, i) => {
      const gap = [0, Math.PI, 0, Math.PI][i] + Math.PI / 2;
      return arc(.5, .5, r, r, gap + .32, gap + Math.PI * 2 - .32, .042);
    }),
    ...[0, 1, 2].map(i => line([[.5 + Math.cos(Math.PI / 2 + (i % 2 ? Math.PI : 0) + .32 * (i % 2 ? -1 : 1)) * (.14 + i * .09), .5 + Math.sin(Math.PI / 2 + (i % 2 ? Math.PI : 0) + .32) * (.14 + i * .09)],
      [.5 + Math.cos(Math.PI / 2 + (i % 2 ? Math.PI : 0) + .32 * (i % 2 ? -1 : 1)) * (.23 + i * .09), .5 + Math.sin(Math.PI / 2 + (i % 2 ? Math.PI : 0) + .32) * (.23 + i * .09)]], .042)),
    dot(.5, .5, .05),
  ] },
];

export const humanTattooLayouts = [
  { id: 'legacy', name: 'Face & forearms' }, { id: 'face', name: 'Face only' },
  { id: 'chest', name: 'Chest piece' }, { id: 'back', name: 'Full back piece' },
  { id: 'sleeves', name: 'Both arm sleeves' }, { id: 'legs', name: 'Thighs & lower legs' },
  { id: 'upper-body', name: 'Chest, back & arms' }, { id: 'full-body', name: 'Full body suit' },
  { id: 'left-side', name: 'Left arm, ribs & leg' },
];
export const humanTattooSizes = [
  { id: 'standard', name: 'Standard', scale: .7 }, { id: 'large', name: 'Large', scale: 1 },
  { id: 'monumental', name: 'Monumental', scale: 1.2 },
];
export const humanTattooColors = [
  { id: 'design', name: 'Design’s own ink' }, { id: 'black', name: 'Black ink', ink: 0x252a30 },
  { id: 'blue', name: 'Indigo ink', ink: 0x304f77 }, { id: 'red', name: 'Crimson ink', ink: 0x823c3b },
  { id: 'green', name: 'Forest ink', ink: 0x345541 }, { id: 'violet', name: 'Violet ink', ink: 0x62446e },
  { id: 'white', name: 'Ivory ink', ink: 0xd6cbb5 },
];

export const isCosmeticSlot = (slot: string) => slot.startsWith('makeup') || slot.startsWith('tattoo');
/** Turning a style or palette on supplies the missing partner, without resetting an existing choice. */
export function chooseHumanCosmetic(parts: Record<string, string>, slot: string, id: string) {
  parts[slot] = id;
  if (slot === 'makeupStyle' && id !== 'none' && (!parts.makeup || parts.makeup === 'none')) parts.makeup = 'rose';
  if (slot === 'makeup' && id !== 'none' && (!parts.makeupStyle || parts.makeupStyle === 'none')) parts.makeupStyle = id === 'warpaint' ? 'warpaint' : 'winged';
}
